import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { useVehicles } from '../../hooks/useVehicles';
import { useAuth } from '../../hooks/useAuth';
import accountService from '../../services/accountService';
import VehicleSelector from '../vehicles/VehicleSelector';
import DriverOnboardingGate from '../auth/DriverOnboardingGate';
import Loader from '../common/Loader';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingState, setOnboardingState] = useState({
    loading: false,
    checked: false,
    completed: true,
    required_fields: [],
    missing_fields: [],
  });
  const [submittingOnboarding, setSubmittingOnboarding] = useState(false);
  const {
    vehicles,
    loading: vehiclesLoading,
    selectedVehicle,
    setDriverVehicleForToday,
    activeVehicleSession,
    selectionRequired,
    loadVehicles,
  } = useVehicles();
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';

  useEffect(() => {
    if (!user?.role) return;

    if (user.role !== 'driver') {
      setOnboardingState({
        loading: false,
        checked: true,
        completed: true,
        required_fields: [],
        missing_fields: [],
      });
      loadVehicles(user.role);
      return;
    }

    let active = true;
    setOnboardingState((prev) => ({ ...prev, loading: true, checked: false }));

    accountService
      .getDriverOnboarding()
      .then((data) => {
        if (!active) return;
        const completed = Boolean(data?.completed);
        setOnboardingState({
          loading: false,
          checked: true,
          completed,
          required_fields: Array.isArray(data?.required_fields) ? data.required_fields : [],
          missing_fields: Array.isArray(data?.missing_fields) ? data.missing_fields : [],
        });

        if (completed) {
          loadVehicles('driver');
        }
      })
      .catch(() => {
        if (!active) return;
        setOnboardingState({
          loading: false,
          checked: true,
          completed: true,
          required_fields: [],
          missing_fields: [],
        });
        loadVehicles('driver');
      });

    return () => {
      active = false;
    };
  }, [user, loadVehicles]);

  const submitOnboarding = async (payload) => {
    setSubmittingOnboarding(true);
    try {
      const result = await accountService.updateDriverOnboarding(payload);
      const completed = Boolean(result?.completed);

      setOnboardingState({
        loading: false,
        checked: true,
        completed,
        required_fields: Array.isArray(result?.required_fields) ? result.required_fields : [],
        missing_fields: Array.isArray(result?.missing_fields) ? result.missing_fields : [],
      });

      if (!completed) {
        toast.error('Please complete all required onboarding fields.');
        return;
      }

      toast.success('Driver onboarding completed.');
      loadVehicles('driver');
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        Object.values(data).forEach((v) => toast.error(Array.isArray(v) ? v[0] : String(v)));
      } else {
        toast.error('Failed to complete onboarding. Please try again.');
      }
    } finally {
      setSubmittingOnboarding(false);
    }
  };

  const shouldSelectVehicle = isDriver && selectionRequired;
  const mustCompleteOnboarding =
    isDriver && (onboardingState.loading || (onboardingState.checked && !onboardingState.completed));

  return (
    <div className="app-layout">
      {sidebarOpen && (
        <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <Navbar onMenuClick={() => setSidebarOpen(true)} />
        <main className="page-content">
          {mustCompleteOnboarding ? (
            <DriverOnboardingGate
              loading={onboardingState.loading}
              status={onboardingState}
              initialProfile={user}
              submitting={submittingOnboarding}
              onSubmit={submitOnboarding}
            />
          ) : isDriver && vehiclesLoading ? (
            <Loader />
          ) : isDriver && !vehiclesLoading && vehicles.length === 0 ? (
            <div className="card" style={{ maxWidth: 620, margin: '2rem auto' }}>
              <div className="card-header">
                <h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>No Vehicle Available</h3>
              </div>
              <div className="card-body">
                <p style={{ margin: 0, color: 'var(--gray-600)' }}>
                  No vehicles are available for selection today. Please contact your fleet owner.
                </p>
              </div>
            </div>
          ) : shouldSelectVehicle ? (
            <VehicleSelector
              vehicles={vehicles}
              loading={vehiclesLoading}
              activeVehicleSession={activeVehicleSession}
              onConfirm={(vehicle) => {
                setDriverVehicleForToday(vehicle, user?.id).catch(() => {});
              }}
            />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}